import { API, apiUrl } from '@/config';
import type { DownloadLink, LicenceView, RevealedKey, SoftwareView } from '@/types';
import { api } from './client';

/**
 * The lab's licence, and the build it can install.
 *
 * As with support, note what no call here takes: the organisation. The server
 * reads it from the token, so there is no parameter that could name another
 * lab and nothing in the UI capable of getting it wrong.
 */
/** The operating systems a build is published for. */
export type Platform = 'windows' | 'linux' | 'macos';

export const licensingService = {
  read(signal?: AbortSignal): Promise<LicenceView> {
    return api.get<LicenceView>(API.endpoints.licence, signal ? { signal } : {});
  },

  /** The newest build for one platform (Windows unless asked). */
  latestVersion(platform: Platform = 'windows', signal?: AbortSignal): Promise<SoftwareView> {
    return api.get<SoftwareView>(
      `${API.endpoints.softwareLatest}?platform=${platform}`,
      signal ? { signal } : {},
    );
  },

  /**
   * The caller's own key, after the password has been re-entered. The server
   * logs every reveal; nothing here caches the result.
   */
  /** `password` is needed only while the caller's reveal check is on. */
  revealKey(password?: string): Promise<RevealedKey> {
    return api.post<RevealedKey>(
      API.endpoints.licenceRevealKey,
      password !== undefined ? { password } : {},
    );
  },

  mintDownload(platform: Platform = 'windows'): Promise<DownloadLink> {
    return api.post<DownloadLink>(API.endpoints.softwareDownloads, { platform });
  },
};

/**
 * Where a minted download token actually points.
 *
 * The server returns a relative path deliberately - behind CloudFront it does
 * not reliably know its own public hostname - so the absolute URL is assembled
 * here, where the API's location is already configured. `path` is API-absolute
 * and already carries the `/api/user` surface, so it is joined to the base URL
 * rather than through `apiUrl`, which would prepend the surface twice.
 */
export function downloadUrl(link: DownloadLink): string {
  return `${API.baseUrl}${link.path}`;
}

// Re-exported so a caller building any other absolute API URL has one way to
// do it rather than reaching for string concatenation.
export { apiUrl };
